<template>
	<component
		:is="tagType"
		class="pronto-btn"
		:class="{
			primary,
			danger,
			link,
			pronto,
			default: isDefaultType,
			pill,
			disabled,
			loading,
			small,
			large,
			block,
		}"
		:disabled="disabled"
		:target="newTab ? '_blank' : null"
		:href="href"
		:to="disabled ? '' : to"
		:replace="replace"
		@click="onClick"
		role="button"
	>
		<component
			v-if="icon"
			:is="icon"
			v-bind="iconProps"
			class="pronto-btn-icon"
		/>
		<slot />
		<Spinner v-if="loading" class="btn-spinner" />
	</component>
</template>

<script>
import { setColorSchemeForElement } from '@/helpers/colors'
import Spinner from './Spinner'
import { colorSchemes } from '@/helpers/constants'

export const types = {
	PRIMARY: 'primary',
	DANGER: 'danger',
	LINK: 'link',
	PRONTO: 'pronto',
}

export const sizes = {
	SMALL: 'small',
	LARGE: 'large',
}

export default {
	name: 'Button',
	components: { Spinner },
	props: {
		// type props are just boolean flags
		...Object.values(types).reduce(
			(acc, t) => ({ ...acc, [t]: { type: Boolean, default: null } }),
			{}
		),
		// size props are just boolean flags
		...Object.values(sizes).reduce(
			(acc, s) => ({ ...acc, [s]: { type: Boolean, default: null } }),
			{}
		),
		dark: { type: Boolean, default: null },
		pill: { type: Boolean, default: null },
		block: { type: Boolean, default: null },
		disabled: { type: Boolean, default: null },
		loading: { type: Boolean, default: null },
		newTab: { type: Boolean, default: null },
		href: String,
		icon: null,
		iconProps: { type: Object, default: () => ({}) },
		to: Object, // https://v3.router.vuejs.org/api/#to
		replace: { type: Boolean, default: null }, // https://v3.router.vuejs.org/api/#replace
	},
	emits: ['click'],
	mounted() {
		if (this.dark) {
			setColorSchemeForElement(this.$el, colorSchemes.DARK)
		}
	},
	computed: {
		tagType() {
			if (this.to) return 'router-link'
			if (this.link || this.href) return 'a'
			return 'span'
		},
		isDefaultType() {
			return Object.values(types).every((t) => !this[t])
		},
	},
	methods: {
		onClick(e) {
			if (this.disabled || this.loading || e.detail === 0) return
			this.$emit('click', e)
		},

		// this exists due to a weird issue we ran into. Enter on the button component would fire an @click event.  The enter would still propagate out and be picked up by other elements like inputs and cause other functions to fire when not wanted.  We now intercept the enter and event another click event.  This allows for us to not be required to have @click & @enter bindings on our buttons both actions will fire @click.
		onEnter(e) {
			if (this.disabled || this.loading) return
			this.$emit('click', e)
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.pronto-btn {
	// default styles
	display: inline-flex;
	align-items: center;
	position: relative;
	gap: 8px;
	padding: 0 16px;
	height: 40px;
	border-radius: 6px;
	background: var(--neutral-0);
	box-shadow: 0 2px 2px 0 var(--neutral-200);
	font-family: Avenir-Black;
	font-size: 14px;
	color: var(--neutral-800);
	cursor: pointer;
	white-space: nowrap;
	max-width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
	text-decoration: none;
	transition: all linear 0.1s;
	transition-property: background, background-color, color;
	outline: 0;
	border: 1px solid var(--neutral-200);
	& + .pronto-btn {
		// if putting buttons in a row, add spacing between them
		margin-left: 8px;
	}
	&:not(.disabled):not(.link):hover {
		background: var(--neutral-100);
		filter: brightness(92%);
	}
	&:not(.disabled):not(.link):active {
		background: var(--neutral-100);
		filter: brightness(88%);
	}
	&.primary {
		background: var(--blue-300);
		color: white;
		padding: 0 24px;
		box-shadow: 0 2px 4px 0 var(--blue-200);
		border: 0;
		&:not(.disabled):hover {
			background-color: var(--blue-300);
			filter: brightness(92%);
		}
		&:not(.disabled):active {
			background-color: var(--blue-300);
			filter: brightness(88%);
		}
	}
	&.pronto {
		background: var(--teal-300);
		box-shadow: 0 2px 4px 0 var(--teal-100);
		color: white;
		border: 0;
		&:not(.disabled):hover {
			background: var(--teal-300);
			filter: brightness(92%);
		}
		&:not(.disabled):active {
			background-color: var(--teal-300);
			filter: brightness(88%);
		}
		&:disabled {
			background-color: var(--teal-100);
		}
	}
	&.link {
		background: transparent;
		color: var(--blue-300);
		box-shadow: none;
		font-family: Avenir-Medium;
		border: 0;
		&:not(.disabled):hover {
			background: transparent;
			color: var(--blue-200);
		}
		&.pronto {
			color: var(--teal-300);
			background: transparent;
			&:not(.disabled):hover {
				color: var(--teal-400);
				background: transparent;
			}
		}
	}
	&.danger {
		background: var(--red-200);
		box-shadow: 0 2px 4px 0 var(--red-100);
		color: white;
		border: 0;
		&:not(.disabled):hover {
			background: var(--red-300);
			filter: brightness(92%);
		}
		&:not(.disabled):active {
			background: var(--red-300);
			filter: brightness(88%);
		}
	}
	&.disabled {
		cursor: not-allowed;
		opacity: 0.65;
	}
	&.loading {
		cursor: wait;
		color: transparent !important;
		.btn-spinner {
			color: white;
			font-size: 28px;
		}
		&.default {
			.btn-spinner {
				color: var(--neutral-800);
			}
		}
		&.large {
			.btn-spinner {
				font-size: 40px;
			}
		}
		&.small {
			.btn-spinner {
				font-size: 20px;
			}
		}
	}
	&.pill {
		border-radius: 24px;
		padding: 0 24px;
	}
	&.small {
		padding: 0 16px !important;
		font-size: 12px;
		height: 32px;
	}
	&.large {
		padding: 0 32px !important;
		font-size: 16px;
		height: 54px;
	}
	&.block {
		width: 100%;
		display: flex;
		justify-content: center;
	}

	:deep(.pronto-spinner) {
		position: absolute;
		left: calc(50% - 0.5em);
		> div {
			background: currentColor;
		}
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.pronto-btn {
		box-shadow: none !important;
		&.default {
			border-color: var(--neutral-200) !important;
			background: var(--neutral-200) !important;
			&:not(.disabled):hover {
				background: var(--neutral-100) !important;
			}
		}
	}
}
</style>
